import React, { createContext, useContext, useState, useCallback } from 'react';
import { cn } from './cn';

type ToastType = 'info' | 'success' | 'error';

interface ToastEntry {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  show: (message: string, opts?: { type?: ToastType; duration?: number }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const show = useCallback((message: string, opts?: { type?: ToastType; duration?: number }) => {
    const id = String(Date.now()) + Math.random().toString(36).slice(2);
    const entry: ToastEntry = { id, message, type: opts?.type ?? 'info', duration: opts?.duration ?? 3500 };
    setToasts((t) => [...t, entry]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), entry.duration);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="fixed bottom-24 right-4 z-[1100] flex flex-col gap-2" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              'rounded-xl border border-light shadow-3 px-4 py-3 max-w-sm text-body backdrop-blur-glass',
              t.type === 'success' && 'bg-accent-emerald/15 text-accent-emerald',
              t.type === 'error' && 'bg-accent-rose/15 text-accent-rose',
              t.type === 'info' && 'bg-bg-3 text-text-primary'
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
